import React from 'react'
import clsx from 'clsx'
import { designTokens } from '../../lib/design-tokens'

type CardVariant = 'default' | 'elevated' | 'outlined' | 'interactive' | 'gaming' | 'glass'
type CardPadding = 'sm' | 'md' | 'lg'

const getCardClasses = (variant: CardVariant, interactive: boolean = false, glow: boolean = false, padding: CardPadding) => {
  const baseClasses = 'card rounded-lg border transition-all duration-200'
  
  const variantClasses = {
    default: 'bg-slate-800 border-slate-700',
    elevated: 'bg-slate-800 border-slate-700 shadow-lg hover:shadow-xl',
    outlined: 'bg-slate-800 border-2 border-slate-600',
    interactive: 'bg-slate-800 border-slate-700 hover:border-slate-600 hover:shadow-md cursor-pointer hover:-translate-y-1',
    gaming: 'bg-gradient-to-br from-slate-800 to-slate-900 border border-orange-500/20 shadow-glow',
    glass: 'backdrop-blur-md bg-slate-800/70 border border-white/10'
  }
  
  const paddingClasses = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  }
  
  const interactiveClasses = interactive ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-transform' : ''
  const glowClasses = glow ? 'shadow-glow animate-pulse' : ''
  
  return clsx(
    baseClasses,
    variantClasses[variant],
    paddingClasses[padding],
    interactiveClasses,
    glowClasses
  )
}

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant
  interactive?: boolean
  glow?: boolean
  padding?: CardPadding
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}
export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {}
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', interactive = false, glow = false, padding = 'md', ...props }, ref) => (
    <div
      ref={ref}
      className={clsx(getCardClasses(variant, interactive, glow, padding), className)}
      {...props}
    />
  )
)

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx('flex flex-col space-y-1.5 pb-4', className)}
      {...props}
    />
  )
)

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={clsx('text-lg font-semibold leading-none tracking-tight text-text-primary', className)}
      {...props}
    >
      {children}
    </h3>
  )
)

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={clsx('text-sm text-text-secondary', className)}
      {...props}
    />
  )
)

const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx('', className)}
      {...props}
    />
  )
)

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx('flex items-center pt-4', className)}
      {...props}
    />
  )
)

Card.displayName = 'Card'
CardHeader.displayName = 'CardHeader'
CardTitle.displayName = 'CardTitle'
CardDescription.displayName = 'CardDescription'
CardBody.displayName = 'CardBody'
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardTitle, CardDescription, CardBody, CardFooter }